/**
 * Closest-neighbor distance for slot cards.
 *
 * Neighbors include real appointments *and* the day's start/end anchor
 * (home or office, from route preferences). The badge used to skip those
 * anchors, which hid a useful signal — last stop of the day 1.6 miles from
 * home looks like a routing win, and dispatchers should see it.
 */

export type RouteAnchor = 'home' | 'office';

export type NeighborKind = 'appointment' | 'start' | 'end';

export interface ClosestNeighbor {
  miles: number;
  kind: NeighborKind;
  /** Set when `kind` is start or end. */
  anchor: RouteAnchor | null;
}

export interface SlotNeighborInput {
  previousAppointmentLocation?: string | null;
  previousAppointmentDistanceMiles?: number | null;
  previousAppointmentStartTime?: string | null;
  previousAppointmentStartTimeUtc?: string | null;
  nextAppointmentLocation?: string | null;
  nextAppointmentDistanceMiles?: number | null;
  nextAppointmentStartTime?: string | null;
  nextAppointmentStartTimeUtc?: string | null;
}

export interface ClosestNeighborOptions {
  /** Override previous-leg miles (client-computed home/office start leg). */
  prevMiles?: number | null;
  /** Override next-leg miles (client-computed home/office end leg). */
  nextMiles?: number | null;
  /** Route-preferences start location. Used when the location string is generic. */
  startAnchor?: RouteAnchor;
  /** Route-preferences end location. Used when the location string is generic. */
  endAnchor?: RouteAnchor;
}

const OFFICE_LOCATION = /\boffice\b|\bbusiness\b/;
const HOME_LOCATION = /\bhome\b/;
const START_ANCHOR_LOCATION =
  /\bhome\b|start of day|workday start|starting location|\boffice\b|\bbusiness\b/;
const END_ANCHOR_LOCATION =
  /\bhome\b|end of day|return to home|ending location|\boffice\b|\bbusiness\b/;

const positiveMiles = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

const hasAnyTime = (times: (string | null | undefined)[]): boolean =>
  times.some((time) => Boolean(time));

export const isStartOfDayNeighbor = (slot: SlotNeighborInput): boolean => {
  const location = (slot.previousAppointmentLocation || '').toLowerCase();
  if (START_ANCHOR_LOCATION.test(location)) return true;
  return !hasAnyTime([slot.previousAppointmentStartTime, slot.previousAppointmentStartTimeUtc]);
};

export const isEndOfDayNeighbor = (slot: SlotNeighborInput): boolean => {
  const location = (slot.nextAppointmentLocation || '').toLowerCase();
  if (END_ANCHOR_LOCATION.test(location)) return true;
  return !hasAnyTime([slot.nextAppointmentStartTime, slot.nextAppointmentStartTimeUtc]);
};

const inferAnchor = (
  location: string | null | undefined,
  fallback: RouteAnchor | undefined,
  defaultAnchor: RouteAnchor,
): RouteAnchor => {
  const loc = (location || '').toLowerCase();
  if (OFFICE_LOCATION.test(loc)) return 'office';
  if (HOME_LOCATION.test(loc)) return 'home';
  return fallback ?? defaultAnchor;
};

export const getClosestNeighbor = (
  slot: SlotNeighborInput,
  options: ClosestNeighborOptions = {},
): ClosestNeighbor | null => {
  const prevMiles = positiveMiles(options.prevMiles ?? slot.previousAppointmentDistanceMiles);
  const nextMiles = positiveMiles(options.nextMiles ?? slot.nextAppointmentDistanceMiles);
  const prevIsAnchor = isStartOfDayNeighbor(slot);
  const nextIsAnchor = isEndOfDayNeighbor(slot);

  const candidates: ClosestNeighbor[] = [];
  if (prevMiles != null) {
    candidates.push({
      miles: prevMiles,
      kind: prevIsAnchor ? 'start' : 'appointment',
      anchor: prevIsAnchor
        ? inferAnchor(slot.previousAppointmentLocation, options.startAnchor, 'home')
        : null,
    });
  }
  if (nextMiles != null) {
    candidates.push({
      miles: nextMiles,
      kind: nextIsAnchor ? 'end' : 'appointment',
      anchor: nextIsAnchor
        ? inferAnchor(slot.nextAppointmentLocation, options.endAnchor, 'home')
        : null,
    });
  }
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.miles !== b.miles) return a.miles - b.miles;
    if (a.kind === 'appointment' && b.kind !== 'appointment') return -1;
    if (b.kind === 'appointment' && a.kind !== 'appointment') return 1;
    return 0;
  });
  return candidates[0];
};

/** Trailing phrase for the green mileage chip, e.g. "to home" or "away". */
export const proximityBadgePhrase = (neighbor: ClosestNeighbor): string => {
  if (neighbor.kind === 'start' && neighbor.anchor) return `from ${neighbor.anchor}`;
  if (neighbor.kind === 'end' && neighbor.anchor) return `to ${neighbor.anchor}`;
  return 'away';
};

export const proximityBadgeTitle = (neighbor: ClosestNeighbor, formattedDistance: string): string => {
  if (neighbor.kind === 'start' && neighbor.anchor) {
    return `${formattedDistance} from the start-of-day location (${neighbor.anchor})`;
  }
  if (neighbor.kind === 'end' && neighbor.anchor) {
    return `${formattedDistance} to the end-of-day location (${neighbor.anchor})`;
  }
  return `Closest neighboring appointment is ${formattedDistance} away`;
};
